import React, { useMemo } from 'react';
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Github,
  Link,
  LinkButton,
} from '@/shared/ui';
import { ExternalLink } from 'lucide-react';

import { formatDateRange, LinkResponse } from '@personal-portfolio/contract';
import { useSiteContent } from '@personal-portfolio/queries';

interface ProjectCardProps {
  title: string;
  description?: string;
  summary?: string;
  type:
    | 'work'
    | 'personal'
    | 'open-source'
    | 'freelance'
    | 'academic'
    | 'hackathon'
    | 'research';
  status:
    | 'planning'
    | 'in-progress'
    | 'completed'
    | 'on-hold'
    | 'cancelled'
    | 'maintenance';
  startDate?: string;
  endDate?: string;
  coverImage?: {
    id: number;
    url: string;
    alternativeText?: string;
  };
  gallery?: Array<{
    id: number;
    url: string;
    alternativeText?: string;
  }>;
  links?: LinkResponse[] | null;
  achievements?: string;
  challenges?: string;
  learnings?: string;
  role?: string;
  teamSize?: number;
  client?: string;
  isFeatured?: boolean;
  isPublic?: boolean;
  id?: string;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  description,
  summary,
  type,
  status,
  startDate,
  endDate,
  coverImage,
  links,
  achievements,
  challenges,
  learnings,
  role,
  teamSize,
  client,
  isFeatured = false,
  id,
}) => {
  const { cards } = useSiteContent();
  const githubLink = useMemo(() => {
    return links?.find((link) => link.type === 'github');
  }, [links]);
  const demoLink = useMemo(() => {
    return links?.find((link) => link.type === 'demo');
  }, [links]);
  const titleLink = demoLink?.url || githubLink?.url;

  return (
    <Card
      className="group @container h-full scroll-mt-20 hover:shadow-md"
      id={id}
    >
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <CardTitle className="text-xl font-semibold tracking-tight">
              {titleLink ? (
                <Link
                  href={titleLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="decoration-muted-foreground/40 underline-offset-4 hover:underline"
                >
                  {title}
                </Link>
              ) : (
                title
              )}
            </CardTitle>
            {summary && (
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {summary}
              </p>
            )}
          </div>
          {isFeatured && (
            <Badge variant="secondary" className="shrink-0">
              {cards.featured}
            </Badge>
          )}
        </div>

        {coverImage && (
          <div className="relative aspect-video overflow-hidden rounded-lg border">
            <img
              src={coverImage.url}
              alt={coverImage.alternativeText || title}
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" className="font-mono text-xs">
            {type}
          </Badge>
          <Badge
            variant={status === 'completed' ? 'default' : 'secondary'}
            className="font-mono text-xs"
          >
            {status}
          </Badge>
          {client && (
            <Badge variant="outline" className="font-mono text-xs">
              {client}
            </Badge>
          )}
          {role && (
            <Badge variant="outline" className="font-mono text-xs">
              {role}
            </Badge>
          )}
          {teamSize && teamSize > 1 && (
            <Badge variant="outline" className="font-mono text-xs">
              {cards.team}: {teamSize}
            </Badge>
          )}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="space-y-2">
          <h4 className="font-mono text-xs font-medium tracking-wider text-muted-foreground uppercase">
            {cards.description}
          </h4>
          <p className="text-sm leading-relaxed break-words text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Project Details (container-relative: two columns only when the
            card itself is wide enough, regardless of viewport) */}
        <div className="grid grid-cols-1 gap-4 text-sm @md:grid-cols-2">
          {startDate && (
            <div>
              <span className="font-medium">{cards.duration}:</span>
              <p className="pt-0.5 font-mono text-xs text-muted-foreground">
                {formatDateRange(startDate, endDate)}
              </p>
            </div>
          )}

          {achievements && (
            <div>
              <span className="font-medium">{cards.achievements}:</span>
              <p className="line-clamp-3 text-muted-foreground">
                {achievements}
              </p>
            </div>
          )}

          {challenges && (
            <div>
              <span className="font-medium">{cards.challenges}:</span>
              <p className="line-clamp-3 text-muted-foreground">{challenges}</p>
            </div>
          )}

          {learnings && (
            <div>
              <span className="font-medium">{cards.learnings}:</span>
              <p className="line-clamp-3 text-muted-foreground">{learnings}</p>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 pt-2">
          {githubLink && githubLink.isVisible && (
            <LinkButton
              variant="outline"
              size="sm"
              className="group/cta"
              href={githubLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Github className="ml-1 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
              {cards.viewCode}
            </LinkButton>
          )}

          {demoLink && demoLink.isVisible && (
            <LinkButton
              variant="default"
              size="sm"
              className="group/cta"
              href={demoLink.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink className="ml-1 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
              {cards.liveDemo}
            </LinkButton>
          )}

          {links
            ?.filter(
              (link) =>
                link !== githubLink && link !== demoLink && link.isVisible,
            )
            .map((link) => (
              <LinkButton
                key={link.url}
                variant="ghost"
                size="sm"
                className="group/cta"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {link.label}
                <ExternalLink className="ml-1 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
              </LinkButton>
            ))}
        </div>
      </CardContent>
    </Card>
  );
};
